import styled from "styled-components";
import {
  CardComp,
  TableCompData,
  ModalCompAdvanceUpdate,
  ButtonComp,
} from "../../../components";
import EmptyTable from "../../../components/Common/EmptyTable";
import Message from "../../../components/MessageModal";
import GroupForm from "./modules/GroupForm";
import AddGroupMemberModal from "./modules/AddGroupMemberModal";
import { BsArrowLeft } from "react-icons/bs";
import { useParams, useNavigate } from "react-router-dom";
import { useState, useMemo } from "react";
import {
  useGetGroupMembersQuery,
  useGetGroupByIdQuery,
} from "../../../store/admin/communicationSlice";
import { useViewMemberQuery } from "../../../store/admin/memberManagement/memberSlice";
import { useGetSingleStaffQuery } from "../../../store/admin/staff";
import { toast } from "react-hot-toast";
import { CommunicationGroupMembersHeader } from "../../../utils/TableHeader";
import { useCommunicationActions } from "./useCommunicationActions";
import TablePagination from "../../../components/Table/Pagination";
import FallbackImage from "../../../components/Common/ImageWrapper";

export const ViewGroup = () => {
  const navigate = useNavigate();
  const [currentMemberId, setCurrentMemberId] = useState();
  const [viewProfileId, setViewProfileId] = useState(null);
  const [viewProfileType, setViewProfileType] = useState(null); // "member" or "staff"
  const [pageNumber, setPageNumber] = useState(1);
  const [modalName, setModalName] = useState();
  const { groupId } = useParams();
  const { editGroup, addMembersToGroup, removeMemberFromGroup } = useCommunicationActions();
  const [isRemoving, setIsRemoving] = useState(false);
  const { data: groupData = {} } = useGetGroupByIdQuery(groupId);
  const {
    data: membersData = {},
    error,
    isLoading,
  } = useGetGroupMembersQuery({
    page: pageNumber,
    groupId: groupId,
  });
  
  // Fetch member details for profile view
  const { data: memberDetails, isLoading: isLoadingMemberDetails } = useViewMemberQuery(
    viewProfileId,
    { skip: !viewProfileId || viewProfileType !== "member" }
  );
  
  // Fetch staff details for profile view
  const { data: staffDetails, isLoading: isLoadingStaffDetails } = useGetSingleStaffQuery(
    viewProfileId,
    { skip: !viewProfileId || viewProfileType !== "staff" }
  );
  
  const { groupMembers, pagination } = useMemo(() => {
    const { communication_group_members = [], pagination } = membersData;
    const perPage = pagination?.perPage || 5;
    const items = communication_group_members.map((item, index) => ({
      ...item,
      serialNumber: index + 1 + (pageNumber - 1) * perPage,
    }));
    return { groupMembers: items, pagination };
  }, [membersData, pageNumber]);

  const updateGroupName = (newGroupName) => {
    setModalName();
    editGroup({ id: groupId, name: newGroupName });
  };

  const addMembers = (members, userType) => {
    setModalName();
    // members array already contains the correct field names (member_id or staff_id)
    // based on userType, so we can pass it directly to the API
    addMembersToGroup(groupId, members);
  };

  const removeMember = async () => {
    if (!currentMemberId) {
      toast.error("Record ID is missing");
      return;
    }
    
    setIsRemoving(true);
    try {
      await removeMemberFromGroup(currentMemberId);
      setModalName();
      setCurrentMemberId(null);
    } catch (error) {
      // Error is handled by toastNotify in useCommunicationActions
    } finally {
      setIsRemoving(false);
    }
  };

  const handleViewProfile = (row) => {
    // Determine if it's a member or staff based on the data structure
    const isStaff = row?.user_type === "staff" || (row?.staff_id && !row?.member_id);
    const userId = isStaff ? row?.staff?.id || row?.staff_id : row?.member?.id || row?.member_id;
    const userType = isStaff ? "staff" : "member";
    
    if (!userId) {
      toast.error("Unable to load profile - user ID not found");
      return;
    }
    
    setViewProfileId(userId);
    setViewProfileType(userType);
    setModalName("View Profile");
  };

  const handleCloseProfile = () => {
    setViewProfileId(null);
    setViewProfileType(null);
    setModalName();
  };

  if (error) {
    toast.error(error?.data?.message || "An error occured, kindly restart app");
  }

  const TableDropDown = [
    {
      name: "View Profile",
      action: (row) => {
        handleViewProfile(row);
      },
    },
    {
      name: "Remove",
      action: (row) => {
        setModalName("Remove from group");
        // Use the CommunicationGroupMember id (row.id), not member.id
        setCurrentMemberId(row?.id);
      },
    },
  ];
  const modalItems = [
    {
      name: "Edit Group",
      component: (
        <GroupForm
          initialValue={groupData?.name}
          submitText="Update group name"
          onBack={() => setModalName()}
          onProceed={updateGroupName}
          title={"Edit Group Name"}
        />
      ),
    },
    {
      name: "Remove from group",
      component: (
        <Message
          title="Remove from group?"
          desc="This action cannot be undone. This user will be removed from this group."
          onBack={() => {
            if (!isRemoving) {
              setModalName();
              setCurrentMemberId(null);
            }
          }}
          onProceed={removeMember}
          loading={isRemoving}
        />
      ),
    },
    {
      name: "Add member or staff",
      component: (
        <AddGroupMemberModal
          title="Add member or staff"
          submitText="Add to group"
          onBack={() => setModalName()}
          onProceed={addMembers}
        />
      ),
    },
    {
      name: "View Profile",
      component: (
        <ProfileModal
          onClose={handleCloseProfile}
          userType={viewProfileType}
          memberData={viewProfileType === "member" ? memberDetails : null}
          staffData={viewProfileType === "staff" ? staffDetails : null}
          isLoading={viewProfileType === "member" ? isLoadingMemberDetails : isLoadingStaffDetails}
        />
      ),
    },
  ];

  return (
    <>
      <Wrapper className="mx-2 mx-lg-4">
        <CardComp
          cardClassName={"pt-4"}
          cardBodyClassName={"py-0 d-flex align-items-center gap-2"}
        >
          <span onClick={() => navigate(-1)}>
            <BsArrowLeft size={30} />
          </span>
          <h5 className="mb-0 fw-bold">{groupData?.name}</h5>
          <img
            role="button"
            src="/Icon/edit2.png"
            alt="edit"
            onClick={() => setModalName("Edit Group")}
          />
        </CardComp>
        <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap mb-3 mt-4">
          <h5 className="fw-bold mb-0" style={{ fontSize: "18px", color: "#383838" }}>
            Group Members
          </h5>
          <ButtonComp
            btnClassName={"px-4 py-2 text-white rounded"}
            btnText={"Add member or staff"}
            onClick={() => setModalName("Add member or staff")}
            ButtonInlineStyle={{
              backgroundColor: "var(--color-primary)",
              borderRadius: "10px",
              border: "none",
              minWidth: "200px",
            }}
          />
        </div>
        <TableCompData
          marginBottom={"5rem"}
          data={groupMembers}
          columns={CommunicationGroupMembersHeader(TableDropDown)}
          cellPadding="12px"
          message={
            isLoading ? (
              <p className="py-4">Loading...</p>
            ) : (
              <EmptyTable
                Message={"No members or staff in this group"}
                btnText={"Add member or staff"}
                onClick={() => setModalName("Add member or staff")}
              />
            )
          }
        />
        {groupMembers.length > 0 && (
          <TablePagination handlePageChange={setPageNumber} {...pagination} />
        )}
        <ModalCompAdvanceUpdate
          pageName={modalName}
          arrayComp={modalItems}
          centered={true}
        />
      </Wrapper>
    </>
  );
};

// Profile Modal Component
const ProfileModal = ({ onClose, userType, memberData, staffData, isLoading }) => {
  const userData = userType === "member" ? memberData : staffData;
  
  if (isLoading) {
    return (
      <div className="p-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="p-4">
        <p>No data available</p>
        <ButtonComp
          btnText="Close"
          onClick={onClose}
          btnClassName="btn btn-secondary"
        />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="fw-bold mb-0">
          {userType === "member" ? "Member" : "Staff"} Profile
        </h5>
        <img
          src="/Icon/cancel.svg"
          role="button"
          onClick={onClose}
          alt="close"
          style={{ cursor: "pointer" }}
        />
      </div>
      
      <CardComp cardClassName="border-0 shadow-sm">
        <div className="row align-items-center">
          <div className="col-md-6">
            <div className="d-flex align-items-center">
              <div className="me-3">
                <FallbackImage
                  src={userData?.photo || "/Images/TopHeader/avatar.svg"}
                  height="80"
                  width="80"
                  className="rounded-circle border border-2"
                  style={{ borderColor: "var(--color-primary)" }}
                  alt="Photo"
                />
              </div>
              <div>
                <h5 className="mb-1 text-dark fw-bold">
                  {userData?.first_name || ""} {userData?.last_name || ""}
                </h5>
                <p className="mb-1 text-muted small">
                  <i className="fas fa-id-card me-1"></i>
                  {userType === "member" ? "Member" : "Staff"} ID: {userData?.member_no || userData?.staff_no || userData?.id || "N/A"}
                </p>
                <p className="mb-0 text-muted small">
                  <i className="fas fa-building me-1"></i>
                  {userData?.branch?.name || "Main Branch"}
                </p>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-2">
              <small className="text-muted d-block">Email</small>
              <span>{userData?.email || "N/A"}</span>
            </div>
            <div className="mb-2">
              <small className="text-muted d-block">Phone</small>
              <span>{userData?.phone || "N/A"}</span>
            </div>
            {userData?.address && (
              <div className="mb-2">
                <small className="text-muted d-block">Address</small>
                <span>{userData?.address}</span>
              </div>
            )}
          </div>
        </div>
      </CardComp>
      
      <div className="mt-3 text-end">
        <ButtonComp
          btnText="Close"
          onClick={onClose}
          btnClassName="px-4 py-2"
          ButtonInlineStyle={{
            borderRadius: "10px",
            backgroundColor: "var(--color-primary)",
            color: "#ffffff",
            border: "none",
            minWidth: "120px",
          }}
        />
      </div>
    </div>
  );
};

const Wrapper = styled.section`
  .modal-content {
    border-radius: 30px;
  }

  /* Improved table styling */
  .rdt_TableRow {
    transition: background-color 0.2s ease;
    
    &:hover {
      background-color: #f7f7f7;
    }
  }

  .rdt_TableCell {
    vertical-align: middle;
  }

  /* Better spacing for member name column */
  .rdt_TableBody .rdt_TableRow .rdt_TableCell:nth-child(2) {
    font-weight: 400;
    color: #45464e;
  }
`;

export default ViewGroup;
